import { QueryClient } from '@tanstack/react-query';

/**
 * React Query client configuration with caching strategy
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache time: 5 minutes
      staleTime: 5 * 60 * 1000,
      // Keep unused data in cache for 10 minutes
      gcTime: 10 * 60 * 1000,
      // Retry failed requests 3 times with exponential backoff
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Refetch on window focus for fresh data
      refetchOnWindowFocus: true,
      // Don't refetch on mount if data is still fresh
      refetchOnMount: false,
      // Refetch on reconnect
      refetchOnReconnect: true,
    },
    mutations: {
      // Retry mutations once
      retry: 1,
      // Show loading state during mutations
      onError: (error) => {
        console.error('Mutation error:', error);
      },
    },
  },
});

/**
 * Query keys for consistent cache management
 */
export const queryKeys = {
  // Auth
  profile: ['auth', 'profile'] as const,

  // Products
  products: (filters?: Record<string, unknown>) =>
    ['products', filters] as const,
  product: (id: string) => ['products', id] as const,

  // Orders
  orders: (filters?: Record<string, unknown>) => ['orders', filters] as const,
  order: (id: string) => ['orders', id] as const,
  orderTracking: (id: string) => ['orders', id, 'tracking'] as const,

  // Analytics
  dashboardStats: (period?: string) =>
    ['analytics', 'dashboard', period] as const,
  salesData: (params: {
    startDate: string;
    endDate: string;
    interval?: string;
  }) => ['analytics', 'sales', params] as const,
  topProducts: (params?: Record<string, unknown>) =>
    ['analytics', 'top-products', params] as const,
  revenueByCategory: (period?: string) =>
    ['analytics', 'revenue-by-category', period] as const,
};
